import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import Table from '../components/common/Table.jsx';
import KpiRow from '../components/optimization/KpiRow.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { IdCell } from '../components/optimization/RecCells.jsx';
import { exportCSV, setScopedItem, STORAGE_KEYS, CONST_SITE_LABEL } from '../components/common/Helper';

import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import DebugService from '../services/DebugService';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const TABS = [
  { key: 'VM',   label: 'VM' },
  { key: 'VMSS', label: 'VMSS' },
];

// ── Helpers ──────────────────────────────────────────────────

const money2 = (v) => `$${Number(v ?? 0).toFixed(2)}`;
const money3 = (v) => `$${Number(v ?? 0).toFixed(3)}`;

// ── Table Columns ────────────────────────────────────────────

const vmColumns = [
  { key: 'id',       label: 'ID', render: (r) => <IdCell row={r} /> },
  { key: 'vmName',   label: 'Name' },
  { key: 'vmSize',   label: 'Size' },
  { key: 'location', label: 'Location' },
  { key: 'onDemandPriceValue', label: 'On Demand cost/hour', className: 'num', render: (r) => money3(r.onDemandPriceValue) },
  { key: 'duration',           label: 'Stopped hours',       className: 'num', render: (r) => (r.duration != null ? r.duration : '—') },
  {
    key: 'totalSavingCost', label: 'Savings cost', className: 'num',
    render: (r) => <span style={{ color: 'var(--green)', fontWeight: 700 }}>{money2(r.totalSavingCost)}</span>,
  },
];

const vmssColumns = [
  { key: 'id',       label: 'ID', render: (r) => <IdCell row={r} /> },
  { key: 'region',   label: 'Region' },
  { key: 'duration', label: 'Stopped hours', className: 'num', render: (r) => (r.duration != null ? r.duration : '—') },
  {
    key: 'totalSavingCost', label: 'Savings cost', className: 'num',
    render: (r) => <span style={{ color: 'var(--green)', fontWeight: 700 }}>{money2(r.totalSavingCost)}</span>,
  },
];

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (vmSaving, vmssSaving) => [
  { id: 1, type: 't1', title: 'VM Total Savings',   value: money2(vmSaving),               subtitle: 'From scheduled stop events' },
  { id: 2, type: 't2', title: 'VMSS Total Savings', value: money2(vmssSaving),             subtitle: 'From scale set scheduling' },
  { id: 3, type: 't3', title: 'Total Savings',      value: money2(vmSaving + vmssSaving),  subtitle: 'VM + VMSS combined' },
];

// ── Main Page ────────────────────────────────────────────────

const AzureInventoryReports = () => {
  const { selectedAccount } = useAuth();

  // Default range: first day of current month → last day of current month (same as the old page).
  const now = new Date();
  const [fromDate, setFromDate] = useState(dayjs(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [toDate,   setToDate]   = useState(dayjs(new Date(now.getFullYear(), now.getMonth() + 1, 0)));

  const [activeTab, setActiveTab] = useState('VM');
  const [isLoading, setIsLoading] = useState(false);

  const [vmRows,   setVmRows]   = useState([]);
  const [vmssRows, setVmssRows] = useState([]);
  const [vmSaving,   setVmSaving]   = useState(0);
  const [vmssSaving, setVmssSaving] = useState(0);
  const [noData,   setNoData]   = useState(false);

  const [vmResApiData,   setVmResApiData]   = useState({});
  const [vmssResApiData, setVmssResApiData] = useState({});

  const fetchData = async () => {
    if (!fromDate || !toDate) return;
    setIsLoading(true);
    setNoData(false);
    setVmSaving(0);
    setVmssSaving(0);

    const dateRequestBody = {
      userAccountId: selectedAccount ?? '',
      fromDate,
      toDate,
    };

    try {
      const [vmRes, vmssRes] = await Promise.all([
        InventoryApiService.getAzureAllSavingCost(APIConstants.ENDPOINT_AZURE_INVENTORY_VM_ALL_EVENT_COST, dateRequestBody),
        InventoryApiService.getAzureAllSavingCost(APIConstants.ENDPOINT_AZURE_INVENTORY_VMSS_ALL_EVENT_COST, dateRequestBody),
      ]);

      DebugService.log('Azure Inventory Report: API response', vmRes);

      setVmResApiData(vmRes?.data ? vmRes : {});
      setVmssResApiData(vmssRes?.data ? vmssRes : {});

      const vmInstances   = vmRes?.data ?? [];
      const vmssInstances = vmssRes?.data ?? [];

      if (!vmInstances.length && !vmssInstances.length) setNoData(true);

      setVmRows(vmInstances.map((i) => ({ ...i, id: i.vmId ?? i.id })));
      setVmssRows(vmssInstances.map((i) => ({ ...i, id: i.instanceId ?? i.id })));

      if (vmRes?.totalSaving)   setVmSaving(vmRes.totalSaving);
      if (vmssRes?.totalSaving) setVmssSaving(vmssRes.totalSaving);
    } catch (error) {
      DebugService.error('Azure Inventory Report: error fetching data', error);
      setVmRows([]); setVmssRows([]); setNoData(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Inventory Reports`;
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedAccount]);

  // Keep AI chatbot context in sync with what's on screen.
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, { vmResApiData, vmssResApiData });
  }, [vmResApiData, vmssResApiData]);

  const kpi = useMemo(() => buildKpiData(vmSaving, vmssSaving), [vmSaving, vmssSaving]);

  const isVM        = activeTab === 'VM';
  const activeRows  = isVM ? vmRows : vmssRows;
  const activeCols  = isVM ? vmColumns : vmssColumns;

  const handleExport = () =>
    exportCSV(activeRows, activeCols, `azure-inventory-report-${activeTab.toLowerCase()}.csv`);

  const onDateChange = (setter) => (e) => setter(e.target.value ? dayjs(e.target.value) : null);

  return (
    <>
      <div className="breadcrumb">
        Inventory <span style={{ color: 'var(--muted-2)' }}>›</span> Azure{' '}
        <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Reports</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Azure Inventory Reports{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} {activeTab.toLowerCase()} events</span>}
          </h1>
          <div className="meta">Realized savings from scheduled start/stop events across VMs and Scale Sets</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={kpi} loading={isLoading} />

      {/* Date range */}
      <div className="filter-bar">
        <div className="field">
          <label>From Date</label>
          <input
            type="date"
            value={fromDate ? fromDate.format('YYYY-MM-DD') : ''}
            max={toDate ? toDate.format('YYYY-MM-DD') : undefined}
            onChange={onDateChange(setFromDate)}
          />
        </div>
        <div className="field">
          <label>To Date</label>
          <input
            type="date"
            value={toDate ? toDate.format('YYYY-MM-DD') : ''}
            min={fromDate ? fromDate.format('YYYY-MM-DD') : undefined}
            onChange={onDateChange(setToDate)}
          />
        </div>
        <div className="spacer" />
        <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
      </div>

      {/* Sub-tabs: VM / VMSS */}
      <div className="sub-tabs">
        {TABS.map((t) => (
          <div key={t.key} className={`sub-tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </div>
        ))}
      </div>

      <Table
        rows={activeRows}
        columns={activeCols}
        loading={isLoading}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="totalSavingCost"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText={noData ? 'No instances found for the selected date' : `No ${activeTab} events found.`}
      />
    </>
  );
};

export default AzureInventoryReports;
